import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CircleEvent } from "@/types/circle";
import { toast } from "sonner";

export function useAdminCircleEvents() {
  const queryClient = useQueryClient();

  const { data: events, isLoading, error } = useQuery({
    queryKey: ["admin-circle-events"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("circle_events")
        .select(`
          *,
          host:profiles!circle_events_host_id_fkey(id, first_name, last_name, email, avatar_url)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as CircleEvent[];
    },
  });

  const approveEvent = useMutation({
    mutationFn: async (eventId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("circle_events")
        .update({
          status: "approved",
          approved_by: user.id,
          approved_at: new Date().toISOString(),
        })
        .eq("id", eventId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-circle-events"] });
      toast.success("Event approved");
    },
    onError: (error) => {
      console.error("Failed to approve event:", error);
      toast.error("Failed to approve event");
    },
  });

  const rejectEvent = useMutation({
    mutationFn: async ({ eventId, reason }: { eventId: string; reason?: string }) => {
      const { error } = await supabase
        .from("circle_events")
        .update({
          status: "rejected",
          cancelled_reason: reason,
        })
        .eq("id", eventId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-circle-events"] });
      toast.success("Event rejected");
    },
    onError: (error) => {
      console.error("Failed to reject event:", error);
      toast.error("Failed to reject event");
    },
  });

  const deleteEvent = useMutation({
    mutationFn: async (eventId: string) => {
      const { error } = await supabase
        .from("circle_events")
        .delete()
        .eq("id", eventId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-circle-events"] });
      toast.success("Event deleted");
    },
    onError: (error) => {
      console.error("Failed to delete event:", error);
      toast.error("Failed to delete event");
    },
  });

  return {
    events: events || [],
    isLoading,
    error,
    approveEvent,
    rejectEvent,
    deleteEvent,
  };
}
